import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  listPendingCommunities,
  listApprovedCommunities,
  approveCommunity,
  listUpgradeRequests,
  approveUpgradeRequest,
} from '../services/onboardingService'
import { API_BASE } from '../config'
import { getPortalRoute, normalizeRole } from '../utils/roleRouting'
import Modal from '../components/Modal'

const ALLOWED_TRIAL_MONTHS = [1, 3, 6, 12, 18, 24]

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem('id_token')
    if (!t) return ''
    const payload = t.split('.')[1]
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return (JSON.parse(json).email || '').toString().toLowerCase()
  } catch {
    return ''
  }
}

/**
 * AppAdminPortal — only accessible to users with role "app_admin".
 *
 * Tabs: Pending | Approved
 *   Each tab has two sub-sections:
 *     1. New Community Requests
 *     2. Plan Upgrade Requests
 */
export default function AppAdminPortal() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('pending')

  // Community registration data
  const [pendingCommunities, setPendingCommunities] = useState([])
  const [approvedCommunities, setApprovedCommunities] = useState([])

  // Upgrade request data
  const [pendingUpgrades, setPendingUpgrades] = useState([])
  const [approvedUpgrades, setApprovedUpgrades] = useState([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(null)
  // Community reject modal
  const [rejectModal, setRejectModal] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  // Upgrade reject modal
  const [upgradeRejectModal, setUpgradeRejectModal] = useState(null) // { requestId, communityId }
  const [upgradeRejectReason, setUpgradeRejectReason] = useState('')

  const idToken = sessionStorage.getItem('id_token') || ''

  useEffect(() => {
    if (!idToken) { navigate('/'); return }
    ;(async () => {
      try {
        const email = decodeIdTokenEmail()
        if (!email) {
          sessionStorage.removeItem('id_token')
          navigate('/access-denied')
          return
        }

        const resp = await fetch(`${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${idToken}` },
        })
        if (!resp.ok) { navigate('/access-denied'); return }

        let data = await resp.json()
        const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || '').toString().toLowerCase()
        if (returned !== email) { navigate('/access-denied'); return }

        if (data.multiCommunity && Array.isArray(data.communities)) {
          const selectedCid = sessionStorage.getItem('community_id') || ''
          const selectedUid = sessionStorage.getItem('user_id') || ''
          const match = data.communities.find(
            c => {
              const cid = c.COMMUNITY_ID || c.communityId || ''
              const uid = c.USER_ID || c.userId || ''
              return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid)
            }
          ) || data.communities[0]
          if (!match) { navigate('/access-denied'); return }
          data = match
        }

        const role = normalizeRole(data.ROLE || data.role)
        const expectedRoute = getPortalRoute(data, { fallback: '/access-denied' })

        if (expectedRoute !== '/app-admin') {
          navigate(expectedRoute)
          return
        }

        sessionStorage.setItem('user_role', role)
        loadAll()
      } catch (err) {
        console.error('AppAdminPortal auth check failed', err)
        navigate('/access-denied')
      }
    })()
  }, [idToken, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true)
    setError('')
    try {
      const [pComm, aComm, pUpgrade, aUpgrade] = await Promise.allSettled([
        listPendingCommunities(idToken),
        listApprovedCommunities(idToken),
        listUpgradeRequests('pending', idToken),
        listUpgradeRequests('approved', idToken),
      ])
      if (pComm.status === 'fulfilled') setPendingCommunities(pComm.value)
      if (aComm.status === 'fulfilled') setApprovedCommunities(aComm.value)
      if (pUpgrade.status === 'fulfilled') setPendingUpgrades(pUpgrade.value)
      if (aUpgrade.status === 'fulfilled') setApprovedUpgrades(aUpgrade.value)
      const firstError = [pComm, aComm, pUpgrade, aUpgrade].find(r => r.status === 'rejected')
      if (firstError) setError(firstError.reason?.message || 'Some data failed to load')
    } catch (err) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  async function handleApprove(communityId, trialMonths = 6) {
    setActionLoading(communityId)
    try {
      await approveCommunity({ communityId, action: 'approve', trialMonths }, idToken)
      setPendingCommunities(prev => prev.filter(c => c.COMMUNITY_ID !== communityId))
    } catch (err) {
      setError(err.message || 'Approval failed')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleReject() {
    if (!rejectModal) return
    setActionLoading(rejectModal)
    try {
      await approveCommunity({ communityId: rejectModal, action: 'reject', reason: rejectReason }, idToken)
      setPendingCommunities(prev => prev.filter(c => c.COMMUNITY_ID !== rejectModal))
      setRejectModal(null)
      setRejectReason('')
    } catch (err) {
      setError(err.message || 'Rejection failed')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleUpgradeApprove(req) {
    const requestId = req.REQUEST_ID || req.requestId
    const communityId = req.COMMUNITY_ID || req.communityId
    const key = 'upg_' + requestId
    setActionLoading(key)
    try {
      await approveUpgradeRequest({ requestId, communityId, action: 'approve' }, idToken)
      setPendingUpgrades(prev => prev.filter(r => (r.REQUEST_ID || r.requestId) !== requestId))
      setApprovedUpgrades(prev => [{ ...req, STATUS: 'APPROVED' }, ...prev])
    } catch (err) {
      setError(err.message || 'Approval failed')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleUpgradeReject() {
    if (!upgradeRejectModal) return
    const { requestId, communityId } = upgradeRejectModal
    const key = 'upg_' + requestId
    setActionLoading(key)
    try {
      await approveUpgradeRequest({ requestId, communityId, action: 'reject', reason: upgradeRejectReason }, idToken)
      setPendingUpgrades(prev => prev.filter(r => (r.REQUEST_ID || r.requestId) !== requestId))
      setUpgradeRejectModal(null)
      setUpgradeRejectReason('')
    } catch (err) {
      setError(err.message || 'Rejection failed')
    } finally {
      setActionLoading(null)
    }
  }

  function handleLogout() {
    sessionStorage.clear()
    navigate('/scm/login')
  }

  const tabs = [
    { key: 'pending', label: 'Pending', count: pendingCommunities.length + pendingUpgrades.length },
    { key: 'approved', label: 'Approved', count: approvedCommunities.length + approvedUpgrades.length },
  ]

  return (
    <div style={styles.root}>
      {/* Header */}
      <div className="safe-top safe-left safe-right" style={styles.headerShell}>
        <div style={styles.header}>
          <div style={styles.headerTitleWrap}>
            <span style={styles.brand}>Smart Community — App Admin</span>
            <span style={styles.brandSubtext}>App Admin Portal</span>
          </div>
          <div style={styles.headerActions}>
            <button style={styles.refreshBtn} onClick={loadAll} disabled={loading}>
            {loading ? 'Loading…' : '↻ Refresh'}
            </button>
            <button style={styles.logoutBtn} onClick={handleLogout}>Sign Out</button>
          </div>
        </div>
      </div>

      <div style={styles.content}>
        <h1 style={styles.pageTitle}>Admin Dashboard</h1>
        <p style={styles.pageSubtitle}>Review community registrations and plan upgrade requests</p>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Tabs */}
        <div style={styles.tabBar}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              style={{ ...styles.tabBtn, ...(activeTab === tab.key ? styles.tabBtnActive : {}) }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  ...styles.tabCount,
                  background: activeTab === tab.key ? '#2563eb' : '#e5e7eb',
                  color: activeTab === tab.key ? '#fff' : '#374151',
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#6b7280', padding: 32 }}>Loading…</p>
        ) : (
          <>
            {activeTab === 'pending' && (
              <TabSection
                communities={pendingCommunities}
                upgrades={pendingUpgrades}
                isPending
                actionLoading={actionLoading}
                onApprove={handleApprove}
                onRejectOpen={(id) => { setRejectModal(id); setRejectReason('') }}
                onUpgradeApprove={handleUpgradeApprove}
                onUpgradeRejectOpen={(req) => { setUpgradeRejectModal({ requestId: req.REQUEST_ID || req.requestId, communityId: req.COMMUNITY_ID || req.communityId }); setUpgradeRejectReason('') }}
              />
            )}
            {activeTab === 'approved' && (
              <TabSection
                communities={approvedCommunities}
                upgrades={approvedUpgrades}
                isPending={false}
              />
            )}
          </>
        )}
      </div>

      {/* Community Reject modal */}
      {rejectModal && (
        <Modal title="Reject Community" onClose={() => setRejectModal(null)}>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Provide an optional reason. This will be included in the rejection email.
          </p>
          <textarea
            style={styles.textarea}
            rows={3}
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.rejectBtn} onClick={handleReject} disabled={actionLoading === rejectModal}>
              {actionLoading === rejectModal ? 'Rejecting…' : 'Confirm Reject'}
            </button>
            <button style={styles.cancelBtn} onClick={() => setRejectModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Upgrade Reject modal */}
      {upgradeRejectModal && (
        <Modal title="Decline Upgrade Request" onClose={() => setUpgradeRejectModal(null)}>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Provide an optional reason. This will be communicated back to the community admin.
          </p>
          <textarea
            style={styles.textarea}
            rows={3}
            placeholder="Reason for declining (optional)"
            value={upgradeRejectReason}
            onChange={e => setUpgradeRejectReason(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              style={styles.rejectBtn}
              onClick={handleUpgradeReject}
              disabled={actionLoading === 'upg_' + upgradeRejectModal?.requestId}
            >
              {actionLoading === 'upg_' + upgradeRejectModal?.requestId ? 'Declining…' : 'Confirm Decline'}
            </button>
            <button style={styles.cancelBtn} onClick={() => setUpgradeRejectModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TabSection({ communities, upgrades, isPending, actionLoading, onApprove, onRejectOpen, onUpgradeApprove, onUpgradeRejectOpen }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      {/* New Community Requests */}
      <div>
        <SectionHeader
          title="New Community Requests"
          count={communities.length}
          color="#1d4ed8"
        />
        {communities.length === 0 ? (
          <EmptyState icon="🏘️" message={`No ${isPending ? 'pending' : 'approved'} community registrations.`} />
        ) : (
          <div style={styles.cardGrid}>
            {communities.map(comm => (
              <CommunityCard
                key={comm.COMMUNITY_ID}
                comm={comm}
                isPending={isPending}
                actionLoading={actionLoading}
                onApprove={onApprove}
                onRejectOpen={onRejectOpen}
              />
            ))}
          </div>
        )}
      </div>

      {/* Plan Upgrade Requests */}
      <div>
        <SectionHeader
          title="Plan Upgrade Requests"
          count={upgrades.length}
          color="#7c3aed"
        />
        {upgrades.length === 0 ? (
          <EmptyState icon="📋" message={`No ${isPending ? 'pending' : 'approved'} upgrade requests.`} />
        ) : (
          <div style={styles.cardGrid}>
            {upgrades.map((req, i) => (
              <UpgradeCard
                key={req.REQUEST_ID || i}
                req={req}
                isPending={isPending}
                actionLoading={actionLoading}
                onApprove={onUpgradeApprove}
                onRejectOpen={onUpgradeRejectOpen}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SectionHeader({ title, count, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>{title}</h2>
      <span style={{
        background: color + '1a', color, border: `1px solid ${color}33`,
        borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600,
      }}>
        {count}
      </span>
    </div>
  )
}

function EmptyState({ icon, message }) {
  return (
    <div style={styles.emptyState}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>{icon}</div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{message}</p>
    </div>
  )
}

function CommunityCard({ comm, isPending, actionLoading, onApprove, onRejectOpen }) {
  const [trialMonths, setTrialMonths] = useState(6)
  const registrantType = (comm.REGISTRANT_USER_TYPE || comm.USER_TYPE || '').toString().toUpperCase()
  const isCommunityAdmin = registrantType === 'COMMUNITY_ADMIN' || registrantType === 'COMMUNITY_STAFF'
  const registrantTypeLabel = registrantType === 'OWNER' ? 'Owner' : isCommunityAdmin ? 'Community Admin' : '—'
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <h2 style={styles.communityName}>{comm.COMMUNITY_NAME}</h2>
          <p style={styles.communityId}>ID: {comm.COMMUNITY_ID}</p>
        </div>
        <span style={isPending ? styles.pendingBadge : styles.approvedBadge}>
          {isPending ? 'Pending' : 'Approved'}
        </span>
      </div>

      <div style={styles.infoGrid}>
        <InfoRow label="Admin" value={`${comm.ADMIN_FIRST_NM || ''} ${comm.ADMIN_LAST_NM || ''}`.trim()} />
        <InfoRow label="Email" value={comm.ADMIN_EMAIL} />
        <InfoRow label="Phone" value={comm.ADMIN_PHONE || comm.PHONE || '—'} />
        <InfoRow label="Registrant Type" value={registrantTypeLabel} />
        {registrantType === 'OWNER' && <InfoRow label="Unit Number" value={comm.REGISTRANT_UNIT_NBR || comm.UNIT_NBR || '—'} />}
        <InfoRow label="Address" value={comm.ADDRESS} />
        <InfoRow label={isPending ? 'Registered' : 'Approved'} value={
          comm.CREATED_AT ? new Date(comm.CREATED_AT).toLocaleDateString() : '—'
        } />
        {comm.PLAN && <PlanBadgeRow plan={comm.PLAN} trialEnd={comm.TRIAL_END} />}
      </div>

      {isPending && (
        <div style={styles.actions}>
          <select
            value={trialMonths}
            onChange={(e) => setTrialMonths(Number(e.target.value))}
            style={styles.trialSelect}
            aria-label="Trial period in months"
          >
            {ALLOWED_TRIAL_MONTHS.map(m => (
              <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
            ))}
          </select>
          <button
            style={styles.approveBtn}
            disabled={actionLoading === comm.COMMUNITY_ID}
            onClick={() => onApprove(comm.COMMUNITY_ID, trialMonths)}
          >
            {actionLoading === comm.COMMUNITY_ID ? 'Processing…' : '✓ Approve Trial'}
          </button>
          <button
            style={styles.rejectBtn}
            disabled={actionLoading === comm.COMMUNITY_ID}
            onClick={() => onRejectOpen(comm.COMMUNITY_ID)}
          >
            ✕ Reject
          </button>
        </div>
      )}
    </div>
  )
}

function UpgradeCard({ req, isPending, actionLoading, onApprove, onRejectOpen }) {
  const key = req.REQUEST_ID || req.requestId
  const isProcessing = actionLoading === 'upg_' + key

  return (
    <div style={{ ...styles.card, borderLeft: `4px solid ${isPending ? '#7c3aed' : '#16a34a'}` }}>
      <div style={styles.cardHeader}>
        <div>
          <h2 style={styles.communityName}>{req.COMMUNITY_NAME || req.communityName || 'Unknown Community'}</h2>
          <p style={styles.communityId}>ID: {req.COMMUNITY_ID || req.communityId}</p>
        </div>
        <span style={isPending ? styles.pendingBadge : styles.approvedBadge}>
          {isPending ? 'Pending' : 'Approved'}
        </span>
      </div>

      <div style={styles.infoGrid}>
        <InfoRow label="Feature" value={req.LABEL || req.label || req.FEATURE || req.feature} />
        <InfoRow label="Requested By" value={req.REQUESTED_BY || req.requestedBy} />
        <InfoRow label="Requested On" value={req.CREATED_AT ? new Date(req.CREATED_AT).toLocaleDateString() : '—'} />
        {!isPending && <InfoRow label="Approved On" value={req.UPDATED_AT ? new Date(req.UPDATED_AT).toLocaleDateString() : '—'} />}
      </div>

      {isPending && (
        <>
          <div style={{
            background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8,
            padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#6d28d9',
          }}>
            📞 Approving will unlock this feature for the community. Ensure payment has been confirmed before approving.
          </div>
          <div style={styles.actions}>
            <button
              style={styles.approveBtn}
              disabled={isProcessing}
              onClick={() => onApprove(req)}
            >
              {isProcessing ? 'Processing…' : '✓ Approve & Unlock'}
            </button>
            <button
              style={styles.rejectBtn}
              disabled={isProcessing}
              onClick={() => onRejectOpen(req)}
            >
              ✕ Decline
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 13, color: '#374151' }}>{value || '—'}</span>
    </div>
  )
}

function PlanBadgeRow({ plan, trialEnd }) {
  const isTrial = (plan || '').toUpperCase() === 'TRIAL'
  let trialInfo = ''
  if (isTrial && trialEnd) {
    const end = new Date(trialEnd)
    const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24))
    trialInfo = daysLeft > 0 ? ` · ${daysLeft}d left` : ' · Expired'
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Plan</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: isTrial ? '#fef9c3' : '#dcfce7',
        color: isTrial ? '#854d0e' : '#15803d',
        border: `1px solid ${isTrial ? '#fde68a' : '#bbf7d0'}`,
        borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600,
        width: 'fit-content',
      }}>
        {isTrial ? '🎉 Trial' : '✅ Paid'}{trialInfo}
      </span>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  root: {
    minHeight: '100vh',
    background: '#f9fafb',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  headerShell: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    background: 'linear-gradient(90deg, #1d4ed8, #60a5fa)',
    color: '#fff',
  },
  header: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '16px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  headerTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  brand: {
    fontWeight: 600,
    fontSize: 16,
    letterSpacing: '-0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  brandSubtext: {
    fontSize: 12,
    color: '#dbeafe',
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.24)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
    cursor: 'pointer',
    minHeight: 44,
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.24)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
    cursor: 'pointer',
    minHeight: 44,
  },
  content: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '32px 16px',
  },
  pageTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
  },
  pageSubtitle: {
    margin: '4px 0 24px',
    fontSize: 14,
    color: '#6b7280',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    borderBottom: '2px solid #e5e7eb',
    marginBottom: 28,
  },
  tabBtn: {
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: -2,
    transition: 'color 0.15s',
  },
  tabBtnActive: {
    color: '#1d4ed8',
    borderBottomColor: '#1d4ed8',
  },
  tabCount: {
    borderRadius: 10,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 700,
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px 24px',
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
  },
  cardGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '20px 24px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  communityName: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
  },
  communityId: {
    margin: '2px 0 0',
    fontSize: 12,
    color: '#9ca3af',
  },
  pendingBadge: {
    background: '#fef9c3',
    color: '#854d0e',
    borderRadius: 12,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 600,
  },
  approvedBadge: {
    background: '#dcfce7',
    color: '#15803d',
    borderRadius: 12,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 600,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 14,
    marginBottom: 4,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    borderTop: '1px solid #f3f4f6',
    paddingTop: 16,
    marginTop: 16,
  },
  trialSelect: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 13,
    color: '#111827',
    background: '#fff',
  },
  approveBtn: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  rejectBtn: {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '9px 20px',
    fontSize: 13,
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: '24px',
    width: '100%',
    maxWidth: 420,
    margin: '0 16px',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  textarea: {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    resize: 'vertical',
    boxSizing: 'border-box',
  },
}
